import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Music2, Loader2 } from 'lucide-react';
import { PlaylistCard } from '../components/PlaylistCard';
import { SubmissionModal } from '../components/SubmissionModal';
import { useGenres } from '../hooks/useGenres';
import { usePlaylists } from '../hooks/usePlaylists';
import { slugify } from '../utils/formatting';
import { Toast } from '../components/Toast';
import { SEO } from '../components/SEO';
import { GenreFeaturedPlaylists } from '../components/GenreFeaturedPlaylists';
import type { TrackSubmission } from '../types';

const genreImage = "https://ftwlzdhkzqfuosdorgjt.supabase.co/storage/v1/object/public/playlistpartner%20public/playlistpartner%20og%20image.png";

export function GenrePage() {
  const { genre } = useParams();
  const decodedGenre = genre ? decodeURIComponent(genre) : '';
  const { genres, loading: genresLoading } = useGenres();
  const selectedGenre = genres.find(g => g.slug === decodedGenre);
  const [selectedPlaylist, setSelectedPlaylist] = useState<any>(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const navigate = useNavigate();

  const { totalCount } = usePlaylists({
    genre: selectedGenre?.name
  });

  const handleViewAll = () => {
    if (!selectedGenre) return;
    
    // Navigate to playlists with genre filters and scroll to top
    window.scrollTo(0, 0);
    navigate('/playlists', { 
      state: { 
        filters: {
          genre: selectedGenre.name,
          fromSubmission: true
        }
      },
      replace: true
    });
  };

  // Show loading state while genres are loading
  if (genresLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Only show not found after genres have loaded
  if (!genresLoading && !selectedGenre) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-4">Genre Not Found</h1>
        <Link to="/" className="text-green-600 hover:text-green-700">
          Return to Home
        </Link>
      </div>
    );
  }

  const seoTitle = `Free ${selectedGenre.name} Spotify Playlist Submission - No Signup`;
  const seoDescription = `Submit your ${selectedGenre.name} tracks to ${totalCount} Spotify playlists for free. Connect with independent curators and grow your audience — no signup needed.`;

  const handleSubmit = async (submission: TrackSubmission) => {
    try {
      // Handle submission success
      setShowSubmissionModal(false);
      setToast({
        type: 'success',
        message: `Track submitted to ${selectedPlaylist?.name}! Watch your inbox for feedback from the curator.`
      });
    } catch (error) {
      console.error('Failed to submit track:', error);
      setToast({
        type: 'error',
        message: 'Failed to submit track. Please try again.'
      });
    }
  };

  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        image={genreImage}
        url={`${window.location.origin}/genre/${selectedGenre.slug}`}
        type="website"
      />

      <main className="container mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to All Genres
        </Link>

        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <Music2 className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2">{selectedGenre.name} Spotify Playlist Submission</h1>
              <p className="text-gray-600 mb-4">{selectedGenre.description}</p>
              <div className="flex gap-4 text-sm text-gray-600">
                <span>{totalCount} Playlists</span>
              </div>
            </div>
          </div>
        </div>

        <GenreFeaturedPlaylists 
          genre={selectedGenre.name}
          totalCount={totalCount}
          onViewAll={handleViewAll}
        />

        {showSubmissionModal && selectedPlaylist && (
          <SubmissionModal
            playlist={selectedPlaylist}
            onClose={() => {
              setShowSubmissionModal(false);
              setSelectedPlaylist(null);
            }}
            onSubmit={handleSubmit}
          />
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </main>
    </>
  );
}